import React, { useState, useEffect } from 'react';
// Native MUI Components
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { Close, Add } from '@mui/icons-material';
import { Apartment, ApartmentType } from '@/types';
import { apartmentService } from '@/services/apartmentService';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/hooks/useI18n';

interface EditApartmentFormProps {
  tripId: string;
  apartment: Apartment;
  apartmentTypes: ApartmentType[];
  trigger: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const EditApartmentForm: React.FC<EditApartmentFormProps> = ({
  tripId,
  apartment,
  apartmentTypes,
  trigger,
  open: externalOpen,
  onOpenChange: externalOnOpenChange
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const { toast } = useToast();
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    name: apartment.name,
    class: apartment.class,
    maxOccupancy: apartment.maxOccupancy,
    apartmentTypeId: apartment.apartmentTypeId,
  });
  const [amenities, setAmenities] = useState<string[]>(apartment.amenities);
  const [newAmenity, setNewAmenity] = useState('');

  // Use external state if provided, otherwise use internal state
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const setIsOpen = externalOnOpenChange || setInternalOpen;

  useEffect(() => {
    setFormData({
      name: apartment.name,
      class: apartment.class,
      maxOccupancy: apartment.maxOccupancy,
      apartmentTypeId: apartment.apartmentTypeId,
    });
    setAmenities(apartment.amenities);
  }, [apartment]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.apartmentTypeId) {
      return;
    }

    try {
      await apartmentService.updateApartment(tripId, apartment.id, {
        ...formData,
        amenities
      });
      toast({
        title: t('apartment.form.apartmentUpdated'),
        description: t('apartment.form.apartmentUpdatedDescription', { name: formData.name }),
      });
      setIsOpen(false);
    } catch (error) {
      toast({
        title: t('apartment.form.errorUpdatingApartment'),
        description: t('apartment.form.errorUpdatingApartmentDescription'),
        variant: "destructive",
      });
    }
  };

  const addAmenity = () => {
    if (newAmenity.trim() && !amenities.includes(newAmenity.trim())) {
      setAmenities([...amenities, newAmenity.trim()]);
      setNewAmenity('');
    }
  };

  const removeAmenity = (amenityToRemove: string) => {
    setAmenities(amenities.filter(amenity => amenity !== amenityToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addAmenity();
    }
  };

  return (
    <>
      <Box onClick={() => setIsOpen(true)} sx={{ cursor: 'pointer' }}>
        {trigger}
      </Box>
      
      <Dialog 
        open={isOpen} 
        onClose={() => setIsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          <Typography variant="h6" component="div">
            {t('apartment.form.editApartment')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setIsOpen(false)}
            sx={{
              color: 'grey.500',
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={t('apartment.form.apartmentName')}
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('apartment.form.apartmentNamePlaceholder')}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>{t('apartment.form.apartmentClass')}</InputLabel>
                  <Select
                    value={formData.class}
                    onChange={(e) => setFormData(prev => ({ ...prev, class: e.target.value as 'economy' | 'standard' | 'premium' }))}
                    label={t('apartment.form.apartmentClass')}
                  >
                    <MenuItem value="economy">{t('apartment.form.classEconomy')}</MenuItem>
                    <MenuItem value="standard">{t('apartment.form.classStandard')}</MenuItem>
                    <MenuItem value="premium">{t('apartment.form.classPremium')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('apartment.form.maxOccupancy')}
                  type="number"
                  inputProps={{ min: 1, max: 20 }}
                  value={formData.maxOccupancy}
                  onChange={(e) => setFormData(prev => ({ ...prev, maxOccupancy: parseInt(e.target.value) }))}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth required>
                  <InputLabel>{t('apartment.form.apartmentType')}</InputLabel>
                  <Select
                    value={formData.apartmentTypeId}
                    onChange={(e) => setFormData(prev => ({ ...prev, apartmentTypeId: e.target.value }))}
                    label={t('apartment.form.apartmentType')}
                  >
                    {apartmentTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
                  {t('apartment.form.amenities')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    fullWidth
                    value={newAmenity}
                    onChange={(e) => setNewAmenity(e.target.value)}
                    placeholder={t('apartment.form.amenityPlaceholder')}
                    onKeyPress={handleKeyPress}
                  />
                  <Button 
                    variant="outlined" 
                    onClick={addAmenity}
                    startIcon={<Add />}
                  >
                    {t('apartment.form.addAmenity')}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {amenities.map((amenity, index) => (
                    <Chip
                      key={index}
                      label={amenity}
                      onDelete={() => removeAmenity(amenity)}
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setIsOpen(false)} 
            variant="outlined"
          >
            {t('apartment.form.cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
          >
            {t('apartment.form.updateApartment')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditApartmentForm;
